import React, { useContext } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AppContext } from '../App'
import { useGet } from '../hooks/useGet'
import { useIsMobile } from '../hooks/useIsMobile'
import { RadAppLayout } from '../common/RadAppLayout'
import { RadSpaceBetween } from '../common/RadSpaceBetween'
import { RadPagination } from '../common/RadPagination'
import { RadInput2 } from '../common/RadInput2'
import { RadButton } from '../common/RadButton'
import { RadBox } from '../common/RadBox'
import { RadGrid } from '../common/RadGrid'
import { SortPopover } from '../shared/SortPopover'
import { CollectionCards } from '../shared/CollectionCards'
import { RadExternalLink } from '../common/RadExternalLink'
import { FancyListHeader } from '../shared/FancyListHeader'
import { BottomPagination } from '../shared/BottomPagination'

export function CollectionList ({ selected }) {
  const pageLength = 20
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { loadingCount } = useContext(AppContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const currentPageIndex = searchParams.get('page') != null ? parseInt(searchParams.get('page')) : 1
  const searchText = searchParams.get('search') ?? ''
  const sortOrder = searchParams.get('sort') ?? 'alphabetical'
  const { data: collections, count } = useGet(`/api/collection?search=${searchText}&limit=${pageLength}&offset=${(currentPageIndex - 1) * pageLength}&sortOrder=${sortOrder}`)
  const { data: userInfo } = useGet('/api/user/current')

  if (collections != null && userInfo != null) {
    const canCreate = userInfo.roles.some((x) => x.name === 'Admin') ||
      userInfo.roles.some((x) => x.permissions.some((y) => y.name === 'Collection Create'))

    return (
      <RadAppLayout
        contentHeader={
          <FancyListHeader
            title='Collections'
            description={
              <RadBox>
                Collections are regularly updated and carefully curated resources around specific topic areas. Learn more about collections, our curation methods, and other useful information on the <RadExternalLink href='https://www.dashconnect.org/'>DASH website.</RadExternalLink>
              </RadBox>
            }
            count={count}
            actions={
              <RadSpaceBetween direction='horizontal' size='xs'>
                <RadButton disabled={!canCreate} onClick={() => navigate('/collection/create')}>Create</RadButton>
              </RadSpaceBetween>
            }
          />
        }
        content={
          <RadSpaceBetween size='l'>
            <RadGrid
              gridDefinition={[
                { colspan: { default: 12, xs: 8 } },
                { colspan: { default: 12, xs: 4 } }
              ]}
            >
              <RadSpaceBetween direction={isMobile ? 'vertical' : 'horizontal'} size='s'>
                <SortPopover sortOrder={sortOrder} loadingCount={loadingCount} />
                <RadBox className='rad-search-input'>
                  <RadInput2
                    initialValue={searchText}
                    placeholder='Search collections to sort by relevance'
                    type='search'
                    onEnter={({ detail }) => {
                      if (detail.value !== '') {
                        searchParams.set('search', detail.value)
                      } else {
                        searchParams.delete('search')
                      }
                      if (detail.value === '') {
                        searchParams.delete('sort')
                      } else {
                        searchParams.set('sort', 'relevance')
                      }
                      searchParams.delete('page')
                      setSearchParams(searchParams)
                    }}
                  />
                </RadBox>
              </RadSpaceBetween>
              <RadBox float={isMobile ? 'none' : 'right'}>
                <RadPagination
                  currentPageIndex={currentPageIndex}
                  pagesCount={Math.ceil(count / pageLength)}
                  onChange={({ detail }) => {
                    searchParams.set('page', detail.currentPageIndex)
                    setSearchParams(searchParams)
                  }}
                  ariaLabels={{
                    nextPageLabel: 'Next page',
                    previousPageLabel: 'Previous page',
                    pageLabel: pageNumber => `Page ${pageNumber} of all pages`
                  }}
                />
              </RadBox>
            </RadGrid>
            <CollectionCards collections={collections} />
            <BottomPagination
              currentPageIndex={currentPageIndex}
              count={count}
              pageLength={pageLength}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
          </RadSpaceBetween>
        }
      />
    )
  }
}
